import { baseApi } from "./baseApi";
import type {
  McpConnectorStatus,
  McpToolPermission as McpWireToolPermission,
} from "../../../shared/mcpConnectorContract";
import {
  readServer,
  readHealth,
  toWireRegisterBody,
  toWireUpdateBody,
  toWireConnectBody,
  type WireServer,
  type McpServerHealth,
} from "./crmMcpWire";

/**
 * Connectors — remote MCP servers registered per customer, connected per user.
 *
 * ## THE TWO STATES, WHICH ARE NOT ONE STATE
 *
 * This is the whole model, and collapsing it is the failure mode:
 *
 *   `enabled` + `status`   describe the SERVER. Customer-level. A manager
 *                          registered it; everyone at that customer sees the same
 *                          value.
 *   `connection.connected` describes ME. User-level. My own bearer token, stored
 *                          against me, never shared.
 *
 * Neither is derived from the other, and two people can correctly see the same
 * healthy server with different connect states. That is not a caching bug.
 *
 * The four cases the UI has to tell apart:
 *
 *   enabled=false                                     → turned off. `status` is
 *                                                       null, NOT "error".
 *   enabled, status="connecting"                      → in progress, not a failure.
 *   enabled, status="error"                            → the SERVER is broken.
 *                                                       Connecting my account will
 *                                                       not fix it.
 *   enabled, status="connected", connected=false      → the server is fine, I
 *                                                       simply have not signed in.
 *
 * The last one must never render like the one above it. "Nobody has connected
 * yet" is a healthy server.
 *
 * ## THE CREDENTIAL IS WRITE-ONLY
 *
 * A user's bearer token goes in through `connectAccount` and never comes back.
 * Not on a read, not in a status detail, not in an error. There is deliberately
 * no field on any type in this file that could hold one after saving — the UI can
 * only ever learn WHETHER a connection exists. Treat it exactly like a password.
 *
 * ## WHY REGISTRATION CARRIES NO PERSON'S TOKEN
 *
 * The remote server enforces the signed-in person's own permissions, and we never
 * re-implement them. One shared customer-wide token would erase that distinction
 * and hand every counselor identical access to whatever the connected system
 * holds. So registering a server takes a URL and a transport; signing in is a
 * separate, per-person act from the composer.
 *
 * Contract agreed with Session B (the server side) and the orchestrator on
 * 2026-08-25; see coord/track-c.md. Session B owns every route here — nothing in
 * this file may grow a second write path.
 */

// The two types below are WIRE VALUES, not local vocabulary — the server
// compares against the same strings. Re-spelling them here is how two ends drift
// apart one literal at a time, so they alias the single contract rather than
// being declared again. Everything that describes OUR shape rather than the
// wire's stays local.

/** Server health. `null` means the server is switched off, NOT that it is broken. */
export type McpServerStatus = McpConnectorStatus;

/**
 * What the agent is allowed to do with one discovered tool.
 *
 * `ask` is the one the UI must surface loudly: it means a person gets
 * interrupted mid-answer, and somebody who does not know that will read the
 * interruption as a fault.
 */
export type McpToolPermission = McpWireToolPermission;

export interface McpServer {
  id: string;
  name: string;
  url: string;
  /** Customer-level: has a manager switched this server on? */
  enabled: boolean;
  /** Server health, shared by everyone at this customer. */
  status: McpServerStatus;
  /**
   * WHY the SERVER is in the state it is, as a stable code, and the ONLY input to
   * what we say about a failure.
   *
   * There is deliberately no free-text detail field beside it. The only human
   * sentence available for a failed probe is the remote server's own error body,
   * and rendering that would put a third party's words in our voice, in our
   * typeface, on our screen — so the server keeps it in the log and an
   * operator-only column, and the UI writes its own wording per code.
   *
   * ⚠️ It is NOT only set on error rows, so never infer trouble from its mere
   * presence. `AUTH_REQUIRED` arrives alongside a HEALTHY `status: "connected"`
   * and means "reachable, and it wants credentials" — a registration waiting for
   * people to sign in, which is the normal state of a server nobody has connected
   * to yet, not a fault.
   *
   * A per-person lapsed sign-in is NOT here — it is `connection.needsReconnect`.
   */
  statusErrorCode?: string | null;
  /**
   * MY connection. User-level, mine alone.
   *
   * `needsReconnect` lives HERE and not on `status`, and that placement is the
   * whole point. It was briefly server-level, which meant one colleague's expired
   * sign-in flipped the shared row amber for everyone the connector still worked
   * for — and sent a manager hunting a fault in a healthy server. A per-person
   * fact belongs on the per-person object.
   *
   * Branch on `needsReconnectCode` if the reason ever matters, never on prose.
   */
  connection: {
    connected: boolean;
    /** THIS caller's sign-in has lapsed. Connecting again is the fix. */
    needsReconnect?: boolean;
    /** EXPIRED_NO_REFRESH | REFRESH_REJECTED */
    needsReconnectCode?: string | null;
  };
  /** How many tools the server advertised, when it last answered. */
  toolCount?: number;
  /**
   * HOW a person signs in here, and therefore what "Connect your account" does.
   *
   * `oauth` sends them to that system's own consent page. `bearer` asks them to
   * paste a token they created there — the FALLBACK, for a server that publishes
   * no OAuth discovery at all.
   *
   * `undefined` means NOT KNOWN YET (the list endpoint does not send it yet).
   * Treating that as `bearer` would keep the real flow permanently hidden, so
   * the connect handler attempts OAuth and falls back on refusal instead.
   */
  authKind?: "bearer" | "oauth";
}

export interface McpServersResponse {
  /** false = the capability is switched off; render the explanation, not an error. */
  enabled?: boolean;
  servers: McpServer[];
}

// The wire ⇄ domain boundary lives in `crmMcpWire.ts`, as named functions rather
// than lambdas inside the endpoint definitions below — see the header there for
// why, and for the four field mismatches that made the case.
export type { McpServerHealth } from "./crmMcpWire";

export interface McpTool {
  name: string;
  /**
   * OPTIONAL, and usually absent. The permissions list is built from the stored
   * permission rows, which carry the tool's name but not the description the
   * remote advertised. Anything rendering this must tolerate its absence rather
   * than leaving an empty line under every tool.
   */
  description?: string;
  permission: McpToolPermission;
}

/**
 * Registration. NO per-user bearer token — see the file header.
 *
 * `transport` is absent on purpose. Streamable HTTP is the only transport that
 * can succeed in v1; SSE is refused at registration and stdio is unrepresentable
 * (it would run a command on our host). A form control whose other options are
 * always refused is worse than a sentence saying what is supported, so the UI
 * states it and sends nothing.
 *
 * `tool_prefix` is absent for a different reason: the server derives it. It
 * namespaces the tool names the model sees, so it has to be unique across the
 * customer's registrations — and uniqueness is only knowable server-side, inside
 * the same transaction that inserts the row. It was briefly required of the
 * caller, which is the one field a client structurally cannot get right: a
 * collision would have failed with an error about a field the operator never saw
 * and could not have set. Supplying one is still honoured; we don't.
 */
export interface RegisterServerBody {
  name: string;
  url: string;
  /** Only where a server needs one. An APP-level credential, never a person's. */
  oauthClientId?: string;
  oauthClientSecret?: string;
}

/**
 * What a rejected write tells the form.
 *
 * `field` names the input to point at. It is our own field name, safe to show;
 * the server deliberately does not pass through the validator's message, because
 * an issue can embed the value that was received — which for a credential is the
 * credential. So the form gets the WHERE from the server and writes its own WHY.
 */
export interface McpWriteError {
  error: string;
  code?: string;
  field?: string;
}

/**
 * One row of the tenant's outbound-host allowlist (V227). `hostPattern` is the
 * NORMALISED spelling the server stored — lower-case, no scheme, port or path —
 * which is also the exact string a DELETE has to name.
 */
export interface EgressAllowlistEntry {
  hostPattern: string;
  note: string | null;
  createdBy: string;
  createdAt: string;
}

/**
 * ⚠️ `unrestricted` IS STATED BY THE SERVER, not inferred here from a zero-length
 * array. An empty list means connectors may reach ANY public host; a client
 * that rendered an empty table would read as the opposite. Every response on
 * this surface carries the flag so the UI reads one field.
 */
export interface EgressAllowlistResponse {
  entries: EgressAllowlistEntry[];
  unrestricted: boolean;
}

export const crmMcpApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The allowlist. READ IS ADMIN-GATED on the server (`manageSettings`), the
     * same as the writes — the list describes where the organisation's data may
     * go, and the caller has to skip this without the role rather than render
     * the refusal as a load fault.
     */
    getEgressAllowlist: builder.query<EgressAllowlistResponse, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/crm/mcp/egress-allowlist", params: { instanceId } }),
      providesTags: ["CrmEgressAllowlist"],
    }),

    /**
     * Add ONE host. The API is per-entry (POST one, DELETE one) — there is no
     * "save the whole list" call, so the card writes as the operator acts rather
     * than holding a draft. The server normalises and validates; a 400 carries
     * the reason in `error`, written to be shown beside the field.
     */
    addEgressAllowlistEntry: builder.mutation<
      EgressAllowlistResponse,
      { instanceId: string; host: string; note?: string }
    >({
      query: ({ instanceId, host, note }) => ({
        url: "/crm/mcp/egress-allowlist",
        method: "POST",
        params: { instanceId },
        body: note ? { host, note } : { host },
      }),
      invalidatesTags: ["CrmEgressAllowlist"],
    }),

    /**
     * Remove ONE host, named in the query string.
     *
     * ⚠️ REMOVING THE LAST ENTRY RE-OPENS EGRESS TO EVERY PUBLIC HOST. The
     * response says so (`unrestricted: true`), and the card asks for an explicit
     * confirmation before calling this on the last row — see AllowedHostsCard.
     */
    removeEgressAllowlistEntry: builder.mutation<
      EgressAllowlistResponse,
      { instanceId: string; host: string }
    >({
      query: ({ instanceId, host }) => ({
        url: "/crm/mcp/egress-allowlist",
        method: "DELETE",
        params: { instanceId, host },
      }),
      invalidatesTags: ["CrmEgressAllowlist"],
    }),

    getMcpServers: builder.query<McpServersResponse, { instanceId: string }>({
      query: ({ instanceId }) => ({ url: "/crm/mcp/servers", params: { instanceId } }),
      // `enabled` is present on BOTH paths, so this reads one field rather than
      // inferring the feature's state from whether the request succeeded.
      //
      // This route briefly answered 404 when the capability was off. That reads
      // as an error, leaving `data` undefined — which renders as "switched on,
      // with nothing configured", the opposite of the truth, and would have made
      // the absence tests below quietly false. It is `{enabled:false}` now, and a
      // 404 here means what a 404 means. Everything that ACTS still 404s, because
      // there is genuinely nothing to register, connect, or toggle.
      transformResponse: (raw: {
        enabled?: boolean;
        servers?: WireServer[];
      }): McpServersResponse => ({
        enabled: raw.enabled ?? true,
        servers: (raw.servers ?? []).map(readServer),
      }),
      providesTags: ["CrmMcpServers"],
    }),

    /**
     * Register a server. CREATE-THEN-TEST, as ONE atomic call: the server probes
     * the URL before it commits, and on failure the row is rolled back. So the
     * caller gets either a working server or nothing — never a half-created one
     * sitting broken in the list. The rejection message is written to be shown
     * inline in the form, and carries no credential.
     */
    registerMcpServer: builder.mutation<
      { id: string } & McpServerHealth,
      { instanceId: string } & RegisterServerBody
    >({
      query: ({ instanceId, ...input }) => ({
        url: "/crm/mcp/servers",
        method: "POST",
        params: { instanceId },
        body: toWireRegisterBody(input),
      }),
      transformResponse: (raw: {
        id: string;
        status?: string | null;
        statusErrorCode?: string | null;
      }) => ({ id: raw.id, ...readHealth(raw) }),
      invalidatesTags: ["CrmMcpServers"],
    }),

    updateMcpServer: builder.mutation<
      { updated: boolean },
      { instanceId: string; id: string; name?: string; enabled?: boolean }
    >({
      query: ({ instanceId, id, ...input }) => ({
        url: `/crm/mcp/servers/${id}`,
        method: "PATCH",
        params: { instanceId },
        body: toWireUpdateBody(input),
      }),
      invalidatesTags: ["CrmMcpServers"],
    }),

    deleteMcpServer: builder.mutation<{ deleted: boolean }, { instanceId: string; id: string }>({
      query: ({ instanceId, id }) => ({
        url: `/crm/mcp/servers/${id}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmMcpServers"],
    }),

    /**
     * Re-probe a registered server. Used by the "Try again" on an errored row.
     * Answers with the new health only; the list refetch carries the rest.
     */
    testMcpServer: builder.mutation<McpServerHealth, { instanceId: string; id: string }>({
      query: ({ instanceId, id }) => ({
        url: `/crm/mcp/servers/${id}/test`,
        method: "POST",
        params: { instanceId },
      }),
      transformResponse: readHealth,
      invalidatesTags: ["CrmMcpServers"],
    }),

    getMcpServerTools: builder.query<
      { enabled?: boolean; tools: McpTool[] },
      { instanceId: string; id: string }
    >({
      query: ({ instanceId, id }) => ({
        url: `/crm/mcp/servers/${id}/tools`,
        params: { instanceId },
      }),
      providesTags: ["CrmMcpTools"],
    }),

    /**
     * The response names the tool as a plain string, not a tool object — the
     * caller already holds the row it just changed.
     */
    setMcpToolPermission: builder.mutation<
      { tool: string; permission: McpToolPermission },
      { instanceId: string; id: string; toolName: string; permission: McpToolPermission }
    >({
      query: ({ instanceId, id, toolName, permission }) => ({
        url: `/crm/mcp/servers/${id}/tools/${encodeURIComponent(toolName)}`,
        method: "PUT",
        params: { instanceId },
        body: { permission },
      }),
      invalidatesTags: ["CrmMcpTools", "CrmMcpServers"],
    }),

    /**
     * Connect MY account. The token goes in here and is never readable again.
     *
     * POST, not PUT, because it is not idempotent — each save re-encrypts a fresh
     * credential. A top-level `/connections` collection rather than a sub-resource
     * of the server row, because a connection belongs to the USER, not to the
     * customer's registration; the URL makes the two-state split visible.
     */
    /**
     * BEGIN the real sign-in: the person goes to that system's own consent page.
     *
     * This is what "Connect your account" should do. Pasting a token is the
     * fallback for a server that publishes no OAuth discovery — see
     * `connectMcpAccount` below.
     *
     * It returns a URL rather than redirecting, because the caller is a fetch,
     * not a navigation. Nothing is stored against anyone here; only coming back
     * through the callback with a code creates a connection.
     *
     * ⚠️ The callback renders its own page and does NOT message the opener, so
     * there is no completion event to await. The caller watches for the window
     * closing and refetches — see `ConnectorsMenu`.
     */
    authorizeMcpAccount: builder.mutation<
      { authorizeUrl: string },
      { instanceId: string; serverId: string }
    >({
      query: ({ instanceId, serverId }) => ({
        url: `/crm/mcp/connections/${serverId}/authorize`,
        method: "POST",
        params: { instanceId },
      }),
      // Deliberately NOT invalidating. Starting a sign-in changes nothing yet,
      // and refetching here would flash the row back to "Not connected" while
      // the person is still on the consent page.
    }),

    connectMcpAccount: builder.mutation<
      { connection: { connected: boolean } },
      { instanceId: string; serverId: string; token: string }
    >({
      query: ({ instanceId, serverId, token }) => ({
        url: `/crm/mcp/connections/${serverId}`,
        method: "POST",
        params: { instanceId },
        body: toWireConnectBody(token),
      }),
      invalidatesTags: ["CrmMcpServers"],
    }),

    disconnectMcpAccount: builder.mutation<
      { connection: { connected: boolean }; removed?: boolean },
      { instanceId: string; serverId: string }
    >({
      query: ({ instanceId, serverId }) => ({
        url: `/crm/mcp/connections/${serverId}`,
        method: "DELETE",
        params: { instanceId },
      }),
      invalidatesTags: ["CrmMcpServers"],
    }),
  }),
});

export const {
  useGetMcpServersQuery,
  useRegisterMcpServerMutation,
  useUpdateMcpServerMutation,
  useDeleteMcpServerMutation,
  useTestMcpServerMutation,
  useGetMcpServerToolsQuery,
  useSetMcpToolPermissionMutation,
  useAuthorizeMcpAccountMutation,
  useConnectMcpAccountMutation,
  useDisconnectMcpAccountMutation,
  useGetEgressAllowlistQuery,
  useAddEgressAllowlistEntryMutation,
  useRemoveEgressAllowlistEntryMutation,
} = crmMcpApi;
